/**
 * Agencies - Profitability Report Page
 * Project profitability analysis for marketing/advertising agencies
 */

import { useEffect } from 'react';
import { MainLayout } from "@/components/MainLayout";
import { ProjectProfitabilityReport } from '@/components/industry/reports';
import { useIndustryProjects } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { exportProfitabilityToCSV, exportProfitabilityToPDF } from '@/lib/report-export';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export default function ProfitabilityPage() {
  useEffect(() => {
    document.title = 'Lucratividade de Projetos | Controle Financeiro';
  }, []);

  const { projects, isLoading } = useIndustryProjects({ domain: CompanyType.AGENCIES });

  // Fetch clients for display
  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .single();

      return data;
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients', company?.id],
    queryFn: async () => {
      if (!company) return [];

      const { data } = await supabase
        .from('clients')
        .select('id, name')
        .eq('company_id', company.id)
        .eq('is_active', true)
        .order('name');

      return data || [];
    },
    enabled: !!company,
  });

  const clientMap = clients.reduce((acc, client) => {
    acc[client.id] = client.name;
    return acc;
  }, {} as Record<number, string>);

  const handleExportCSV = () => {
    const reportData = (projects || []).map((project) => ({
      project,
      budgetRevenue: project.budget_revenue || 0,
      actualRevenue: project.actual_revenue || 0,
      budgetCost: project.budget_cost || 0,
      actualCost: project.actual_cost || 0,
      actualProfit: (project.actual_revenue || 0) - (project.actual_cost || 0),
      actualMargin: project.actual_revenue ? ((project.actual_revenue - project.actual_cost) / project.actual_revenue) * 100 : 0,
      clientName: project.client_id ? clientMap[project.client_id] : undefined,
    }));

    exportProfitabilityToCSV(reportData, 'lucratividade-projetos-agencia.csv');
  };

  const handleExportPDF = () => {
    const reportData = (projects || []).map((project) => ({
      project,
      budgetRevenue: project.budget_revenue || 0,
      actualRevenue: project.actual_revenue || 0,
      budgetCost: project.budget_cost || 0,
      actualCost: project.actual_cost || 0,
      actualProfit: (project.actual_revenue || 0) - (project.actual_cost || 0),
      actualMargin: project.actual_revenue ? ((project.actual_revenue - project.actual_cost) / project.actual_revenue) * 100 : 0,
      clientName: project.client_id ? clientMap[project.client_id] : undefined,
    }));

    const totals = reportData.reduce(
      (acc, item) => ({
        budgetRevenue: acc.budgetRevenue + item.budgetRevenue,
        actualRevenue: acc.actualRevenue + item.actualRevenue,
        budgetCost: acc.budgetCost + item.budgetCost,
        actualCost: acc.actualCost + item.actualCost,
        budgetProfit: acc.budgetProfit + (item.budgetRevenue - item.budgetCost),
        actualProfit: acc.actualProfit + item.actualProfit,
      }),
      { budgetRevenue: 0, actualRevenue: 0, budgetCost: 0, actualCost: 0, budgetProfit: 0, actualProfit: 0 }
    );

    exportProfitabilityToPDF(reportData, totals);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Carregando relatório...</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto py-6">
        <ProjectProfitabilityReport
          projects={projects || []}
          clientMap={clientMap}
          onExportCSV={handleExportCSV}
          onExportPDF={handleExportPDF}
        />
      </div>
    </MainLayout>
  );
}
